// src/components/QuickTrade.js
import React, { useContext, useMemo, useEffect, useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Box,
    ToggleButton,
    ToggleButtonGroup,
    Backdrop,
    Skeleton,
    InputAdornment, // Added for Slippage TextField
    Select, // Added for layout
    FormControl, // Added for Wallet/Group selector
    InputLabel, // Added for Wallet/Group selector
    MenuItem, // Added for Wallet/Group selector
    ListSubheader, // Added for Wallet/Group selector
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { transformToBTC } from '../../utils/helpers'; // Assuming this helper exists

import { WalletsContext } from "../../state/context/wallets.context";
import { AuthContext } from "../../state/context/auth.context";
import { useQuickTradeForm, useQuickTradeLogic } from "../../hooks";

// Custom styled ToggleButton for a more distinct look
const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        margin: theme.spacing(0.5),
        border: 0,
        '&.Mui-disabled': {
            border: 0,
        },
        '&:not(:first-of-type)': {
            borderRadius: theme.shape.borderRadius,
        },
        '&:first-of-type': {
            borderRadius: theme.shape.borderRadius,
        },
    },
}));

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'white',
        backgroundColor: theme.palette.primary.main,
    },
    '&.Mui-selected': {
        backgroundColor: theme.palette.secondary.main,
    },
    color: theme.palette.text.primary,
}));


/**
 * TokenTrade Component
 * @param {object} props - The component props.
 * @param {string} props.tokenId - The ID of the token to be traded.
 * @param {number} [props.maxAmount=null] - The maximum sellable amount for the token (optional, defaults to null).
 */
const TokenTrade = ({ tokenId }) => {
    // --- Local Component State for Token Data ---
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null); // Renamed to avoid conflict with form validation error
    const [maxAmount, setMaxAmount] = useState(null);
    const { t } = useTranslation();

    const price = data?.price || 0; // Default to 0 if data is not available
    const ticker = data?.ticker;

    // --- Context ---
    const { state: authState } = useContext(AuthContext);
    const { state: walletsState, getWallets } = useContext(WalletsContext);

    const { isConnected } = authState.user || {};
    const { wallets: walletList = [], walletGroups = [], mainWallet } = walletsState;


    // --- Effects ---
    useEffect(() => {
        if (!walletList.length) {
            getWallets();
        }
    }, []);


    // --- Memoized Wallets for Form Hook ---
    const favoriteWallets = useMemo(() => {
        let wallets = walletList.filter((wallet) => wallet.isFavorite);
        if (mainWallet) {
            // Add main wallet to favorites if it exists, ensuring uniqueness
            if (!wallets.some((wallet) => wallet._id === mainWallet._id)) {
                wallets = [...wallets, mainWallet];
            }
        }
        return wallets;
    }, [walletList, mainWallet]);

    // --- Use Custom Form Hook ---
    const {
        tradeType,
        selectedWalletOrGroupId,
        amount, // This is the quantity from the form
        slippage,
        handleTradeTypeChange,
        handleWalletChange,
        handleAmountChange,
        handleSlippageChange,
        isValidAmount,
        isValidSlippage,
        ODIN_MAIN_WALLET_ID,
    } = useQuickTradeForm(maxAmount, walletList, walletGroups, mainWallet, favoriteWallets);

    // Determine if the trade button should be enabled
    const canTrade = isConnected && selectedWalletOrGroupId && isValidAmount && isValidSlippage;

    // --- Use Custom Logic Hook ---
    const { isTrading, handleQuickTrade } = useQuickTradeLogic(
        tokenId,
        tradeType,
        selectedWalletOrGroupId,
        amount,
        slippage,
        walletList,
        mainWallet,
        canTrade // Pass canTrade directly to the logic hook
    );

    // --- Fetch Token Data Effect ---
    useEffect(() => {
        if (!tokenId) {
            setLoading(false);
            setFetchError('No Token ID provided.');
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setFetchError(null);
            setData(null);
            try {
                const response = await fetch(`https://api.odin.fun/v1/token/${ tokenId }`);
                if (!response.ok) throw new Error(`Failed to fetch token data: ${ response.status }`);
                const result = await response.json();
                setData(result);
            } catch (err) {
                setFetchError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [tokenId]);

    // --- Fetch User Token Balance Effect ---
    useEffect(() => {
        const fetchUserBalance = async () => {
            if (!selectedWalletOrGroupId || !tokenId || !isConnected) {
                setMaxAmount(null);
                return;
            }

            try {
                // Get the actual wallet ID for the API call
                let walletUserId = null;

                if (selectedWalletOrGroupId === ODIN_MAIN_WALLET_ID && mainWallet) {
                    walletUserId = mainWallet.userId;
                } else {
                    const selectedWallet = walletList.find(wallet => wallet._id === selectedWalletOrGroupId);
                    if (selectedWallet) {
                        walletUserId = selectedWallet.userId;
                    }
                }

                if (!walletUserId) {
                    setMaxAmount(null);
                    return;
                }

                // Use 'btc' as tokenId when buying, use actual tokenId when selling
                const balanceTokenId = tradeType === 'buy' ? 'btc' : tokenId;

                const response = await fetch(`https://api.odin.fun/v1/balance/${ balanceTokenId }/${ walletUserId }`);
                if (response.ok) {
                    const result = await response.json();
                    if (result.data && typeof result.data.balance) {
                        // Balance is returned directly as a number
                        const balance = result.data.balance / 1e11;
                        setMaxAmount(balance);
                    } else {
                        setMaxAmount(0);
                    }
                } else {
                    setMaxAmount(null);
                }
            } catch (err) {
                console.warn('Failed to fetch user token balance:', err);
                setMaxAmount(null);
            }
        };

        fetchUserBalance();
    }, [selectedWalletOrGroupId, tokenId, isConnected, ODIN_MAIN_WALLET_ID, mainWallet, walletList, tradeType]);

    // Memoize the total calculation to avoid re-computing on every render
    const total = useMemo(() => {
        const numAmount = parseFloat(amount);
        if (isNaN(numAmount) || numAmount <= 0 || price === 0) {
            return 0;
        }
        // Calculation logic based on tradeType and price scaling
        if (tradeType === 'sell') {
            // Selling 'amount' of ticker, converting to BTC. Price is assumed to be in smallest units (e.g., satoshis for BTC)
            return (numAmount) * (price / 1e11);
        }
        // Buying 'amount' of BTC, converting to ticker. Price is assumed to be in smallest units.
        return (numAmount * 1e11) / price;
    }, [amount, price, tradeType]);

    // --- Handlers ---
    // This handler will now just update the tradeType in the form hook
    const handleActionChange = (event, newAction) => {
        if (newAction !== null) {
            handleTradeTypeChange(newAction);
        }
    };

    // --- Conditional Render: Loading, Error, Not Connected, No Wallets ---
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                <Skeleton variant="text" width="80%" sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={150} sx={{ borderRadius: 2 }} />
            </Box>
        );
    }

    if (fetchError) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="error">Error: {fetchError}</Typography>
            </Box>
        );
    }

    const noWalletsAvailable = favoriteWallets.length === 0 && walletGroups.length === 0 && !mainWallet;
    const showOverlay = !isConnected || noWalletsAvailable;

    // --- Main Render: Token Trade Controls ---
    return (
        <Card sx={{ minWidth: 275, maxWidth: 400, margin: 'auto', boxShadow: 3, borderRadius: 2, position: 'relative' }}>
            {showOverlay && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(2px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                        borderRadius: 2,
                    }}
                >
                    <Box sx={{ textAlign: 'center', p: 3 }}>
                        <Typography variant="h6" color="white" sx={{ mb: 1, fontWeight: 'bold' }}>
                            {t('trading.quickTradeUnavailable')}
                        </Typography>
                        <Typography variant="body2" color="grey.300">
                            {!isConnected
                                ? t('trading.connectWalletToTrade')
                                : t('trading.createWalletFirst')}
                        </Typography>
                    </Box>
                </Box>
            )}
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" component="div" gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('trading.quickTrade')} {ticker}/BTC
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                    {t('trading.currentPrice')}: {transformToBTC(price)}
                </Typography>

                <Box sx={{ my: 3, display: 'flex', justifyContent: 'center' }}>
                    <StyledToggleButtonGroup
                        value={tradeType} // Use tradeType from hook
                        exclusive
                        onChange={handleActionChange}
                        aria-label="trade action"
                    >
                        <StyledToggleButton value="buy" aria-label="buy">
                            {t('trading.buy')}
                        </StyledToggleButton>
                        <StyledToggleButton value="sell" aria-label="sell">
                            {t('trading.sell')}
                        </StyledToggleButton>
                    </StyledToggleButtonGroup>
                </Box>

                {/* Wallet/Group Selector */}
                <FormControl size="small" fullWidth sx={{ mb: 2 }}> {/* Added fullWidth and mb for spacing */}
                    <InputLabel id="quicktrade-wallet-label">{t('trading.walletGroup')}</InputLabel>
                    <Select
                        labelId="quicktrade-wallet-label"
                        name="wallet"
                        value={selectedWalletOrGroupId}
                        onChange={handleWalletChange}
                        displayEmpty
                        renderValue={(selected) => {
                            if (!selected) {
                                return (
                                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                        {t('trading.selectWalletGroup')}
                                    </Typography>
                                );
                            }
                            const walletObj = walletList.find((wallet) => wallet._id === selected);
                            const groupObj = walletGroups.find((group) => group._id === selected);
                            if (selected === ODIN_MAIN_WALLET_ID && mainWallet) {
                                return mainWallet.name || t('trading.mainWallet');
                            }
                            return walletObj ? walletObj.name : groupObj ? `${ groupObj.name } (Group)` : "Unknown";
                        }}
                    >
                        <MenuItem value="" disabled>
                            <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                {t('trading.selectWalletGroup')}
                            </Typography>
                        </MenuItem>

                        {/* Main Wallet Option */}
                        {mainWallet && (
                            <MenuItem key={ODIN_MAIN_WALLET_ID} value={ODIN_MAIN_WALLET_ID}>
                                {mainWallet.name || t('trading.mainWallet')}
                            </MenuItem>
                        )}

                        {/* Favorite Wallets Group */}
                        {favoriteWallets.length > 0 && <ListSubheader>{t('trading.favorites')}</ListSubheader>}
                        {favoriteWallets.map((wallet) => (
                            <MenuItem key={wallet._id} value={wallet._id}>
                                {wallet.name}
                            </MenuItem>
                        ))}

                        {/* Wallet Groups Group */}
                        {walletGroups.length > 0 && <ListSubheader>{t('trading.groups')}</ListSubheader>}
                        {walletGroups.map((group) => (
                            <MenuItem key={group._id} value={group._id}>
                                {group.name} (Group)
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Amount Input */}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <TextField
                        fullWidth
                        id="amount-input"
                        label={`${ t('trading.quantityIn') } ${ tradeType === 'buy' ? "BTC" : ticker }`}
                        variant="outlined"
                        type="text" // Use text to manage non-numeric input gracefully
                        inputMode="decimal"
                        value={amount} // Use amount from hook
                        onChange={handleAmountChange} // Use handleAmountChange from hook
                        error={!isValidAmount && amount !== ""} // Use isValidAmount from hook
                        helperText={
                            !isValidAmount && amount !== ""
                                ? parseFloat(amount) <= 0
                                    ? t('trading.amountMustBePositive')
                                    :  maxAmount !== null
                                        ? `${ t('trading.maxAmount') }: ${ maxAmount } ${ ticker }`
                                        : ""
                                :  maxAmount !== null
                                    ? `${ t('trading.available') } ${ t('trading.balance') }: ${ maxAmount } ${ tradeType === "sell" ? ticker : "BTC" }`
                                    : ""
                        }
                    />
                    {tradeType === "sell" && maxAmount !== null && maxAmount > 0 && (
                        <Button
                            variant="outlined"
                            onClick={() => handleAmountChange({ target: { value: maxAmount.toString() } })}
                            sx={{
                                minWidth: 'auto',
                                px: 2,
                                alignSelf: 'flex-start',
                                mt: 0.5
                            }}
                        >
                            Max
                        </Button>
                    )}
                </Box>

                {/* Slippage Input */}
                <TextField
                    fullWidth
                    label={t('trading.slippage')}
                    size="small"
                    variant="outlined"
                    value={slippage} // Use slippage from hook
                    onChange={handleSlippageChange} // Use handleSlippageChange from hook
                    type="text"
                    inputMode="decimal"
                    InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{
                        maxLength: 5,
                    }}
                    error={!isValidSlippage && slippage !== ""} // Use isValidSlippage from hook
                    helperText={!isValidSlippage && slippage !== "" ? t('trading.slippageRange') : ""}
                    sx={{ mb: 2 }}
                />

                <Box sx={{ mt: 2, mb: 2, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                        {t('trading.totalEstimated')}:
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                        {total.toFixed(8)} {tradeType === 'buy' ? ticker : 'BTC'}
                    </Typography>
                </Box>

                {/* Quick Trade Button */}
                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleQuickTrade} // Use handleQuickTrade from hook
                    disabled={!canTrade || isTrading} // Use canTrade and isTrading from hooks
                    sx={{
                        py: 1.5,
                        textTransform: 'capitalize',
                        fontWeight: 'bold',
                        backgroundColor: tradeType === 'buy' ? 'success.main' : 'error.main',
                        '&:hover': {
                            backgroundColor: tradeType === 'buy' ? 'success.dark' : 'error.dark',
                        },
                        opacity: (!canTrade || isTrading) ? 0.6 : 1, // Adjust opacity for disabled state
                        cursor: (!canTrade || isTrading) ? "not-allowed" : "pointer",
                        "&.Mui-disabled": {
                            pointerEvents: "auto", // Allows tooltip on disabled elements if needed
                            cursor: "not-allowed",
                        },
                    }}
                >
                    {isTrading ? `${ tradeType === 'buy' ? t('trading.buying') : t('trading.selling') }...` : `${ tradeType === 'buy' ? t('trading.buy') : t('trading.sell') } ${ ticker }`}
                </Button>
            </CardContent>
        </Card>
    );
};

export default React.memo(TokenTrade);
